import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import {
  MdNotifications, MdDone, MdDoneAll, MdClose,
  MdWarning, MdInfo, MdRefresh, MdPayment,
} from 'react-icons/md';
import { notificationsApi, renewalApi, subscriptionApi } from '../../api/notificationsApi';
import { selectToken, selectRole, selectPermissions } from '../../store/authSlice';

// == Helpers and Constants ==

const TYPE_ICON = {
  WARNING: <MdWarning  size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />,
  RENEWAL: <MdPayment  size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />,
  SYSTEM:  <MdInfo     size={16} className="text-purple-500 flex-shrink-0 mt-0.5" />,
  INFO:    <MdInfo     size={16} className="text-neutral-400 flex-shrink-0 mt-0.5" />,
};

const TYPE_BG = {
  WARNING: 'bg-amber-50 border-amber-100',
  RENEWAL: 'bg-blue-50 border-blue-100',
  SYSTEM:  'bg-purple-50 border-purple-100',
  INFO:    'bg-white border-neutral-100',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const PLAN_LABEL = {
  MONTHLY:     { label: 'Monthly',     months: '1 month'   },
  QUARTERLY:   { label: 'Quarterly',   months: '3 months'  },
  HALF_YEARLY: { label: 'Half-Yearly', months: '6 months'  },
  YEARLY:      { label: 'Yearly',      months: '12 months' },
};

// == Renewal Modal ============================================================
// Improvement 1: plan prices are fetched LIVE when the modal opens, not
// read from stale notification metadata. UPI details still come from metadata
// (they won't change between when the notification was sent and now).

export function RenewalModal({ metadata, onClose, initialIncludeOutstanding = false }) {
  const parsedMeta = React.useMemo(() => {
    try { return JSON.parse(metadata || '{}'); } catch { return {}; }
  }, [metadata]);

  // UPI details: prefer notification metadata (pre-populated when opened from bell)
  // Fall back to live fetch when opened from Dashboard (metadata is null)
  const [upiSettings,      setUpiSettings]      = useState(null);
  const { upiId: metaUpiId, upiName: metaUpiName, upiPaymentEnabled: metaUpiEnabled } = parsedMeta;
  const upiId            = metaUpiId      || upiSettings?.upiId;
  const upiName          = metaUpiName    || upiSettings?.upiName;
  const upiPaymentEnabled = metaUpiEnabled !== undefined ? metaUpiEnabled : upiSettings?.upiPaymentEnabled;

  const [plans,    setPlans]    = useState([]);
  const [planLoad, setPlanLoad] = useState(true);
  const [step,     setStep]     = useState('plan');   // 'plan' | 'payment' | 'done'
  const [selected, setSelected] = useState(null);
  const [txRef,    setTxRef]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  // Outstanding due (fetched on mount — self-contained)
  const [pendingDue,         setPendingDue]         = useState(null);
  const [includeOutstanding, setIncludeOutstanding] = useState(initialIncludeOutstanding);

  // Fetch live plan prices + pending due on mount
  useEffect(() => {
    subscriptionApi.getRenewalPlans()
      .then(data => {
        const paid = data.filter(p => p.planType !== 'TRIAL' && p.isActive);
        setPlans(paid);
      })
      .catch(() => setError('Could not load plan prices. Please try again.'))
      .finally(() => setPlanLoad(false));

    // Fetch pending due — if outstanding was already cleared since Dashboard loaded,
    // auto-uncheck the "include outstanding" flag so the plain renewal can proceed.
    subscriptionApi.getPendingDue()
      .then(data => {
        if (data?.hasDue) {
          setPendingDue(data);
        } else {
          // Outstanding is zero — force-uncheck the flag regardless of initialIncludeOutstanding
          setPendingDue(null);
          setIncludeOutstanding(false);
        }
      })
      .catch(() => {});

    // If opened from Dashboard (no metadata), fetch UPI settings live
    if (!metaUpiId) {
      renewalApi.getPaymentSettings()
        .then(d => setUpiSettings(d))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed totals
  const planAmount        = selected?.amount ?? 0;
  const outstandingAmount = pendingDue?.pendingAmount ?? 0;
  const totalAmount       = includeOutstanding ? planAmount + outstandingAmount : planAmount;

  async function handleSubmit() {
    if (!txRef.trim()) { setError('Please enter the UPI transaction reference.'); return; }
    // Allow only letters, digits, spaces, hyphens, forward-slashes (covers UPI/IMPS/NEFT/RTGS)
    const TX_PATTERN = /^[A-Za-z0-9 /\-]{1,50}$/;
    if (!TX_PATTERN.test(txRef.trim())) {
      setError('Transaction reference must contain only letters, digits, spaces, hyphens, or slashes (max 50 characters).');
      return;
    }
    setSaving(true); setError('');
    try {
      await renewalApi.submit({
        planType:            selected.planType,
        amount:              totalAmount,
        currency:            selected.currency,
        paymentMethod:       'UPI',
        transactionRef:      txRef.trim(),
        includesOutstanding: includeOutstanding || undefined,
        outstandingAmount:   includeOutstanding ? outstandingAmount : undefined,
      });
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <h3 className="text-sm font-semibold text-neutral-800">Renew Subscription</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100">
            <MdClose size={18} />
          </button>
        </div>

        <div className="p-5">
          {step === 'plan' && (
            <>
              <p className="text-xs text-neutral-500 mb-3">Choose a renewal plan:</p>
              {planLoad ? (
                <p className="text-xs text-neutral-400 text-center py-4">Loading plans…</p>
              ) : (
                <div className="space-y-2 mb-4">
                  {plans.length === 0 && (
                    <p className="text-xs text-neutral-400">No plans available. Contact support.</p>
                  )}
                  {plans.map(p => (
                    <button key={p.planType}
                      onClick={() => setSelected(p)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition
                        ${selected?.planType === p.planType
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-neutral-200 hover:border-blue-300 hover:bg-neutral-50'}`}>
                      <div>
                        <p className="text-sm font-medium text-neutral-800">
                          {PLAN_LABEL[p.planType]?.label ?? p.planType}
                        </p>
                        <p className="text-xs text-neutral-500">{PLAN_LABEL[p.planType]?.months}</p>
                        {p.baseAmount > p.amount && (
                          <p className="text-[11px] text-green-700 mt-0.5">
                            Referral reward applied
                            {p.rewardType === 'DISCOUNT_PERCENT' && p.rewardValue != null
                              ? ` (${p.rewardValue}% off)`
                              : p.rewardType === 'DISCOUNT_AMOUNT' && p.rewardValue != null
                                ? ` (₹${Number(p.rewardValue).toLocaleString('en-IN')} off)`
                                : ''}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        {p.baseAmount > p.amount && (
                          <p className="text-[11px] text-neutral-400 line-through">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency', currency: p.currency || 'INR', minimumFractionDigits: 0,
                            }).format(p.baseAmount)}
                          </p>
                        )}
                        <p className="text-sm font-semibold text-neutral-800">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency', currency: p.currency || 'INR', minimumFractionDigits: 0,
                          }).format(p.amount)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Outstanding toggle — only shown when there is a pending balance */}
              {pendingDue && selected && (
                <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 flex-shrink-0"
                    checked={includeOutstanding}
                    onChange={e => setIncludeOutstanding(e.target.checked)}
                  />
                  <div>
                    <p className="text-xs font-semibold text-amber-800">
                      Also clear outstanding ₹{new Intl.NumberFormat('en-IN').format(pendingDue.pendingAmount)} in this transfer
                    </p>
                    <p className="text-[10px] text-amber-700 mt-0.5">
                      Pay both in a single UPI transfer.
                      Total to pay:{' '}
                      <strong>
                        {new Intl.NumberFormat('en-IN', { style: 'currency', currency: pendingDue.currency || 'INR', minimumFractionDigits: 0 }).format(
                          (selected?.amount ?? 0) + pendingDue.pendingAmount
                        )}
                      </strong>
                    </p>
                  </div>
                </label>
              )}

              {selected?.rewardType && selected.baseAmount > selected.amount && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <p className="text-xs font-semibold text-green-800">Referral reward will be applied to this renewal.</p>
                  <p className="text-[10px] text-green-700 mt-0.5">
                    Original plan: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: selected.currency || 'INR', minimumFractionDigits: 0 }).format(selected.baseAmount)}
                    {' '}→ Discounted: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: selected.currency || 'INR', minimumFractionDigits: 0 }).format(selected.amount)}
                  </p>
                </div>
              )}

              <button disabled={!selected || planLoad}
                onClick={() => setStep('payment')}
                className="btn-primary w-full text-sm py-2 disabled:opacity-50">
                Continue
              </button>
            </>
          )}

          {step === 'payment' && (
            <>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                {includeOutstanding ? (
                  <>
                    <p className="text-xs font-semibold text-blue-800 mb-1">Combined payment breakdown:</p>
                    <div className="text-xs text-blue-700 space-y-0.5">
                      <div className="flex justify-between">
                        <span>{PLAN_LABEL[selected.planType]?.label} renewal</span>
                        <span>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: selected.currency || 'INR', minimumFractionDigits: 0 }).format(planAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Outstanding balance</span>
                        <span>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: pendingDue?.currency || 'INR', minimumFractionDigits: 0 }).format(outstandingAmount)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-blue-900 border-t border-blue-200 pt-1 mt-1">
                        <span>Total to pay</span>
                        <span>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: selected.currency || 'INR', minimumFractionDigits: 0 }).format(totalAmount)}</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-blue-600 mt-1">One transfer clears both renewal + outstanding.</p>
                  </>
                ) : (
                  <p className="text-xs font-semibold text-blue-800 mb-1">
                    Pay{' '}
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency', currency: selected.currency || 'INR', minimumFractionDigits: 0,
                    }).format(totalAmount)}{' '}
                    for {PLAN_LABEL[selected.planType]?.label}
                  </p>
                )}
                {upiPaymentEnabled && upiId ? (
                  <div className="mt-2 space-y-2">
                    <p className="text-xs text-blue-700 font-medium">Scan QR code or use UPI ID:</p>
                    <div className="flex items-center gap-4">
                      <div className="bg-white p-2 rounded-lg border border-blue-200 flex-shrink-0">
                        <QRCodeSVG
                          value={`upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(upiName || 'UnifiedOps')}&am=${totalAmount}&cu=${selected?.currency || 'INR'}&tn=${encodeURIComponent(includeOutstanding ? 'Renewal+Outstanding' : 'Subscription+Renewal')}`}
                          size={96}
                          level="M"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] text-blue-600 mb-0.5">UPI ID</p>
                        <p className="text-sm font-mono font-semibold text-blue-900 break-all">{upiId}</p>
                        {upiName && <p className="text-xs text-blue-600 mt-0.5">{upiName}</p>}
                      </div>
                    </div>
                    <p className="text-[10px] text-neutral-400">
                      Open GPay / PhonePe / Paytm → Scan QR or pay to UPI ID above.
                      Amount is pre-filled in the QR.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-neutral-500">
                    Please contact your administrator for payment details.
                  </p>
                )}
              </div>

              <div className="mb-3">
                <label className="block text-xs font-medium text-neutral-600 mb-1">
                  Transaction / UTR Reference <span className="text-red-500">*</span>
                </label>
                <input
                  className="input w-full text-sm"
                  placeholder="e.g. 421234567890"
                  value={txRef}
                  maxLength={50}
                  onChange={e => {
                    // Strip any character not in the allowed set before storing
                    const sanitized = e.target.value.replace(/[^A-Za-z0-9 /\-]/g, '');
                    setTxRef(sanitized);
                    setError('');
                  }}
                />
                <p className="text-[10px] text-neutral-400 mt-0.5">
                  Enter the 12-digit UTR / transaction ID from your UPI app.
                </p>
              </div>

              {error && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">{error}</p>
              )}

              <div className="flex gap-2">
                <button onClick={() => setStep('plan')} className="btn-secondary flex-1 text-sm py-2">
                  Back
                </button>
                <button onClick={handleSubmit} disabled={saving}
                  className="btn-primary flex-1 text-sm py-2 disabled:opacity-50">
                  {saving ? 'Submittingâ€¦' : 'Submit Renewal'}
                </button>
              </div>
            </>
          )}

          {step === 'done' && (
            <div className="py-6 text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <MdDone size={24} className="text-green-600" />
              </div>
              <p className="text-sm font-semibold text-neutral-800 mb-1">Request submitted!</p>
              <p className="text-xs text-neutral-500 mb-4">
                Your renewal request is under review.
                {includeOutstanding && ' Your outstanding balance will also be cleared upon approval.'}
                {' '}You'll receive a notification once it's processed.
              </p>
              <button onClick={onClose} className="btn-primary text-sm px-6">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// == Main NotificationBell component ============================================================

export default function NotificationBell() {
  const accessToken    = useSelector(selectToken);
  const role           = useSelector(selectRole);
  const permissions    = useSelector(selectPermissions);
  const isSuperAdmin   = role === 'SUPER_ADMIN';

  const [open,         setOpen]         = useState(false);
  const [data,         setData]         = useState({ notifications: [], unreadCount: 0 });
  const [loading,      setLoading]      = useState(false);
  const [renewalNotif, setRenewalNotif] = useState(null);
  const ref = useRef(null);

  // == Fetch notifications ============================================================

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const result = await notificationsApi.getAll();
      setData(result);
    } catch {
      // silently ignore â€” bell shouldn't break the page
    } finally {
      setLoading(false);
    }
  }, []);

  // == Improvement 3: SSE instead of polling ============================================================
  // Super-admins don't have tenant notifications, so skip SSE for them.
  useEffect(() => {
    if (!accessToken || isSuperAdmin) return;

    // Initial fetch on mount
    fetchNotifications();

    // Open SSE stream â€” server pushes a 'notification' event on new arrivals
    const es = notificationsApi.openStream(accessToken);

    es.addEventListener('notification', () => {
      fetchNotifications();
    });

    es.onerror = () => {
      // EventSource auto-reconnects â€” nothing to do here
    };

    return () => es.close();
  }, [accessToken, isSuperAdmin, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // == Actions ============================================================

  async function handleMarkRead(id, e) {
    e.stopPropagation();
    await notificationsApi.markRead(id);
    setData(d => ({
      ...d,
      notifications: d.notifications.map(n => n.id === id ? { ...n, read: true } : n),
      unreadCount:   Math.max(0, d.unreadCount - 1),
    }));
  }

  async function handleMarkAllRead() {
    await notificationsApi.markAllRead();
    setData(d => ({
      ...d,
      notifications: d.notifications.map(n => ({ ...n, read: true })),
      unreadCount:   0,
    }));
  }

  // Improvement 5: auto mark-all-read when the dropdown is opened
  async function handleOpenBell() {
    const wasOpen = open;
    setOpen(v => !v);
    if (!wasOpen) {
      fetchNotifications();
      // If there are unread items, silently mark them read after a brief delay
      // so the user has a moment to see the badge update before it clears
      if (data.unreadCount > 0) {
        setTimeout(async () => {
          try {
            await notificationsApi.markAllRead();
            setData(d => ({
              ...d,
              notifications: d.notifications.map(n => ({ ...n, read: true })),
              unreadCount: 0,
            }));
          } catch { /* silent */ }
        }, 1500);
      }
    }
  }

  // Don't render for super-admin (they have no tenant notifications)
  if (isSuperAdmin) return null;

  // Backend already strips RENEWAL notifications for STAFF/DOCTOR/READ_ONLY.
  // Use the list as-is — no role check needed here.
  const { notifications, unreadCount } = data;

  return (
    <>
      <div ref={ref} className="relative">
        <button
          onClick={handleOpenBell}
          className="relative p-1.5 rounded-lg hover:bg-neutral-100 transition"
          aria-label="Notifications"
        >
          <MdNotifications size={22} className={unreadCount > 0 ? 'text-blue-600' : 'text-neutral-500'} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white
                             text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border
                          border-neutral-100 z-50 flex flex-col max-h-[480px]">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
              <span className="text-sm font-semibold text-neutral-800">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-2 text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">
                    {unreadCount} new
                  </span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={fetchNotifications} title="Refresh"
                  className="p-1 rounded hover:bg-neutral-100 text-neutral-400">
                  <MdRefresh size={15} />
                </button>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} title="Mark all as read"
                    className="p-1 rounded hover:bg-neutral-100 text-neutral-400">
                    <MdDoneAll size={15} />
                  </button>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1">
              {loading && notifications.length === 0 ? (
                <p className="text-xs text-neutral-400 text-center py-8">Loadingâ€¦</p>
              ) : notifications.length === 0 ? (
                <p className="text-xs text-neutral-400 text-center py-8">No notifications yet.</p>
              ) : (
                notifications.map(n => (
                  <div key={n.id}
                    className={`px-4 py-3 border-b last:border-0 flex gap-2 transition
                      ${!n.read ? TYPE_BG[n.type] ?? TYPE_BG.INFO : 'bg-white border-neutral-50'}`}>
                    {TYPE_ICON[n.type] ?? TYPE_ICON.INFO}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium leading-snug ${!n.read ? 'text-neutral-900' : 'text-neutral-600'}`}>
                        {n.title}
                      </p>
                      <p className="text-[11px] text-neutral-500 mt-0.5 leading-snug line-clamp-2">
                        {n.message}
                      </p>
                      {/* Optional banner image stored in metadata.imageUrl */}
                      {(() => {
                        try {
                          const meta = JSON.parse(n.metadata || '{}');
                          // Only render https:// URLs â€” block javascript:, data:, and relative paths
                          if (meta.imageUrl && /^https:\/\/.+/i.test(meta.imageUrl)) return (
                            <img
                              src={meta.imageUrl}
                              alt=""
                              className="mt-1.5 w-full rounded-lg object-cover max-h-28"
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          );
                        } catch { /* ignore */ }
                        return null;
                      })()}
                      <div className="flex items-center justify-between mt-1.5 gap-2">
                        <span className="text-[10px] text-neutral-400">{timeAgo(n.createdAt)}</span>
                        <div className="flex items-center gap-1">
                          {n.type === 'RENEWAL' && (() => {
                            try {
                              const meta = JSON.parse(n.metadata || '{}');
                              // APPROVED: only show "Renew Now" once we're within 7 days of expiry
                              if (meta.outcome === 'APPROVED') {
                                if (!meta.renewBefore) return null;
                                const renewDate = new Date(meta.renewBefore);
                                renewDate.setHours(0, 0, 0, 0);
                                if (new Date() < renewDate) return null;
                              }
                              // REJECTED or reminder (no outcome): always show
                            } catch { /* show button on parse error */ }
                            return (
                              <button
                                onClick={() => { setRenewalNotif(n); setOpen(false); }}
                                className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded font-medium hover:bg-blue-700">
                                Renew Now
                              </button>
                            );
                          })()}
                          {!n.read && (
                            <button onClick={e => handleMarkRead(n.id, e)}
                              title="Mark as read"
                              className="p-0.5 rounded hover:bg-neutral-200 text-neutral-400">
                              <MdDone size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {renewalNotif && (
        <RenewalModal
          metadata={renewalNotif.metadata}
          onClose={() => { setRenewalNotif(null); fetchNotifications(); }}
        />
      )}
    </>
  );
}
